/**
 * The profile fields a classification rule can search for keywords (#472).
 *
 * Shared because both ends need the same list for different reasons: the backend
 * maps each scope to a column in its match SQL, the admin form renders one
 * checkbox + keyword input per scope. A scope added on one side only would either
 * be unreachable from the UI or accepted by the API and silently never matched.
 *
 * `company_size` is absent, and no longer as a technicality: the rule carried a
 * numeric size window alongside these scopes, and it was dropped in review: an
 * admin reading a tag back could not tell whether the size filter or the keywords
 * put it there. Company size came back in #724 as a condition KIND of its own
 * (`company-size-bands.ts`), ticked bands rather than a window, and ANDed with the
 * keyword conditions like any other: never a field, so never an OR with one.
 */

export const CLASSIFICATION_SCOPES = [
    "job_title",
    "headline",
    "about",
    "company_name",
    "company_industry",
    "past_experience_title",
] as const;

export type ClassificationScope = (typeof CLASSIFICATION_SCOPES)[number];

/**
 * Labels for the admin form. The task names these scopes from the admin's point
 * of view ("current job title", "description", "industries"), while the columns
 * carry the database's names; these are the former, and the only strings an
 * admin ever reads.
 */
export const CLASSIFICATION_SCOPE_LABELS: Record<ClassificationScope, string> = {
    job_title: "Current job title",
    headline: "Current headline",
    // NOT "Description". `profiles` carries two description-shaped columns:
    // `about`, which is the paragraph the PERSON writes about themselves on
    // LinkedIn, and `company_description`, which is the company's blurb and is not
    // a classification scope at all. "Description" reads as the second, and the
    // task's own SFO example relies on that reading: it puts "Single Family Office"
    // under Description, terms an executive rarely writes in their own bio and
    // their firm always writes in its. Naming the field for what it holds is the
    // whole fix available here.
    about: "Profile summary",
    company_name: "Current company name",
    company_industry: "Industries",
    past_experience_title: "Past experience titles",
};

/**
 * The same scopes named for the middle of a sentence.
 *
 * The form labels above head a field, where "Current job title" is right. The
 * consequence panel reads a rule back as prose ("to profiles whose company name
 * and job title both match") and the form labels lowercased give "current company
 * name and current job title", where the repeated "current" is noise the reader has
 * to step over twice in one line.
 *
 * Two maps rather than a rule for deriving one from the other: "Profile summary"
 * and "Industries" carry no prefix to strip, and a `replace(/^current /)` would be
 * a mechanism standing in for six words of copy.
 */
export const CLASSIFICATION_SCOPE_SENTENCE_LABELS: Record<ClassificationScope, string> = {
    job_title: "job title",
    headline: "headline",
    about: "profile summary",
    company_name: "company name",
    company_industry: "industry",
    past_experience_title: "past experience titles",
};
